import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import api, { getImageUrl } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface Friend {
  friendship_id: string;
  friend_id: string;
  display_name: string;
  profile_slug: string | null;
  profile_image_url: string | null;
  friends_since: string;
}

interface FriendRequest {
  friendship_id: string;
  from_user_id?: string;
  to_user_id?: string;
  display_name: string;
  profile_slug: string | null;
  profile_image_url: string | null;
  created_at: string;
}

interface SearchResult {
  id: string;
  display_name: string;
  profile_slug: string | null;
  profile_image_url: string | null;
  friendship_status: string | null;
  friendship_id: string | null;
  request_direction: 'sent' | 'received' | null;
}

interface FriendBooking {
  id: string;
  scheduled_date: string;
  scheduled_time: string;
  status: string;
  created_at: string;
  booker_name?: string;
  booker_profile_image?: string | null;
  host_name?: string;
  host_profile_image?: string | null;
}

export default function Friends() {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [incomingBookings, setIncomingBookings] = useState<FriendBooking[]>([]);
  const [outgoingBookings, setOutgoingBookings] = useState<FriendBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState<'friends' | 'requests' | 'bookings' | 'search'>('friends');

  const fetchFriends = async () => {
    try {
      const [friendsRes, requestsRes, sentRes, bookingsRes] = await Promise.all([
        api.get('/friends'),
        api.get('/friends/requests'),
        api.get('/friends/sent'),
        api.get('/friends/bookings')
      ]);
      setFriends(friendsRes.data.friends);
      setRequests(requestsRes.data.requests);
      setSentRequests(sentRes.data.requests);
      setIncomingBookings(bookingsRes.data.incoming);
      setOutgoingBookings(bookingsRes.data.outgoing);
    } catch {
      setError('Failed to load friends');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFriends();
  }, []);

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;

    setIsSearching(true);
    try {
      const response = await api.get(`/friends/search?q=${encodeURIComponent(searchQuery)}`);
      console.log('Search response:', response.data);
      setSearchResults(response.data.users);
    } catch (err: unknown) {
      console.error('Search error:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const sendRequest = async (userId: string) => {
    try {
      await api.post('/friends/request', { userId });
      setSuccess('Friend request sent');
      setSearchQuery('');
      setSearchResults([]);
      fetchFriends();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to send request');
    }
  };

  const acceptRequest = async (friendshipId: string) => {
    try {
      await api.post(`/friends/${friendshipId}/accept`);
      setSuccess('Friend request accepted');
      fetchFriends();
    } catch {
      setError('Failed to accept request');
    }
  };

  const declineRequest = async (friendshipId: string) => {
    try {
      await api.post(`/friends/${friendshipId}/decline`);
      setSuccess('Friend request declined');
      fetchFriends();
    } catch {
      setError('Failed to decline request');
    }
  };

  const removeFriend = async (friendshipId: string) => {
    if (!confirm('Remove this friend?')) return;

    try {
      await api.delete(`/friends/${friendshipId}`);
      setSuccess('Friend removed');
      fetchFriends();
    } catch {
      setError('Failed to remove friend');
    }
  };

  const cancelRequest = async (friendshipId: string) => {
    try {
      await api.delete(`/friends/${friendshipId}`);
      setSuccess('Request cancelled');
      fetchFriends();
    } catch {
      setError('Failed to cancel request');
    }
  };

  const confirmBooking = async (bookingId: string) => {
    try {
      await api.post(`/friends/bookings/${bookingId}/confirm`);
      setSuccess('Booking confirmed');
      fetchFriends();
    } catch {
      setError('Failed to confirm booking');
    }
  };

  const declineBooking = async (bookingId: string) => {
    try {
      await api.post(`/friends/bookings/${bookingId}/decline`);
      setSuccess('Booking declined');
      fetchFriends();
    } catch {
      setError('Failed to decline booking');
    }
  };

  const cancelBooking = async (bookingId: string) => {
    if (!confirm('Cancel this booking?')) return;
    try {
      await api.delete(`/friends/bookings/${bookingId}`);
      setSuccess('Booking cancelled');
      fetchFriends();
    } catch {
      setError('Failed to cancel booking');
    }
  };

  const pendingBookingsCount = incomingBookings.filter(b => b.status === 'pending').length;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title mb-0">Friends</h1>
        <div className="text-sm text-gray-400">
          {friends.length} friend{friends.length !== 1 ? 's' : ''}
          {requests.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-600 text-white rounded-full text-xs">
              {requests.length} pending
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="bg-green-900/50 text-green-300 px-4 py-3 rounded-lg text-sm border border-green-800">
          {success}
          <button onClick={() => setSuccess('')} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#2a2a4a]">
        <button
          onClick={() => setActiveTab('friends')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'friends'
              ? 'border-[#1111d4] text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Friends ({friends.length})
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'requests'
              ? 'border-[#1111d4] text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Requests
          {requests.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-red-600 text-white rounded-full text-xs">
              {requests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('bookings')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'bookings'
              ? 'border-[#1111d4] text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Bookings
          {pendingBookingsCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-orange-600 text-white rounded-full text-xs">
              {pendingBookingsCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'search'
              ? 'border-[#1111d4] text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Find Friends
        </button>
      </div>

      {/* Friends List */}
      {activeTab === 'friends' && (
        <div className="space-y-3">
          {friends.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-400 mb-4">No friends yet</p>
              <button
                onClick={() => setActiveTab('search')}
                className="text-[#1111d4] hover:underline"
              >
                Find friends to add
              </button>
            </div>
          ) : (
            friends.map((friend) => (
              <div key={friend.friendship_id} className="card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {friend.profile_image_url ? (
                      <img src={getImageUrl(friend.profile_image_url) || ''} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#252550] flex items-center justify-center text-xl flex-shrink-0">
                        {friend.display_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="text-white font-medium truncate">{friend.display_name}</h3>
                      {friend.profile_slug && (
                        <a
                          href={`/p/${friend.profile_slug}`}
                          className="text-sm text-gray-400 hover:text-[#1111d4]"
                        >
                          View profile
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <a
                      href={`/friends/${friend.friend_id}/book`}
                      className="btn-primary text-sm flex-1 sm:flex-none text-center"
                    >
                      Book Game
                    </a>
                    <button
                      onClick={() => removeFriend(friend.friendship_id)}
                      className="btn-secondary text-sm flex-1 sm:flex-none"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-6">
          {/* Received Requests */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-3">Received Requests</h2>
            {requests.length === 0 ? (
              <p className="text-gray-400 text-sm">No pending requests</p>
            ) : (
              <div className="space-y-3">
                {requests.map((request) => (
                  <div key={request.friendship_id} className="card flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {request.profile_image_url ? (
                        <img src={getImageUrl(request.profile_image_url) || ''} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#252550] flex items-center justify-center text-xl">
                          {request.display_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="text-white font-medium">{request.display_name}</h3>
                        <p className="text-sm text-gray-400">Wants to be friends</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => acceptRequest(request.friendship_id)}
                        className="btn-primary text-sm"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => declineRequest(request.friendship_id)}
                        className="btn-secondary text-sm"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sent Requests */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-3">Sent Requests</h2>
            {sentRequests.length === 0 ? (
              <p className="text-gray-400 text-sm">No pending sent requests</p>
            ) : (
              <div className="space-y-3">
                {sentRequests.map((request) => (
                  <div key={request.friendship_id} className="card flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {request.profile_image_url ? (
                        <img src={getImageUrl(request.profile_image_url) || ''} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#252550] flex items-center justify-center text-xl">
                          {request.display_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="text-white font-medium">{request.display_name}</h3>
                        <p className="text-sm text-gray-400">Pending...</p>
                      </div>
                    </div>
                    <button
                      onClick={() => cancelRequest(request.friendship_id)}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name..."
              className="input flex-1"
            />
            <button
              onClick={handleSearch}
              disabled={searchQuery.length < 2 || isSearching}
              className="btn-primary"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="space-y-3">
              {searchResults.map((user) => (
                <div key={user.id} className="card flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {user.profile_image_url ? (
                      <img src={getImageUrl(user.profile_image_url) || ''} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#252550] flex items-center justify-center text-xl">
                        {user.display_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3 className="text-white font-medium">{user.display_name}</h3>
                      {user.profile_slug && (
                        <span className="text-sm text-gray-400">@{user.profile_slug}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    {user.friendship_status === 'accepted' ? (
                      <span className="px-3 py-1 bg-green-900/30 text-green-300 rounded text-sm">
                        Friends
                      </span>
                    ) : user.friendship_status === 'pending' ? (
                      <span className="px-3 py-1 bg-yellow-900/30 text-yellow-300 rounded text-sm">
                        {user.request_direction === 'sent' ? 'Request Sent' : 'Accept Request'}
                      </span>
                    ) : (
                      <button
                        onClick={() => sendRequest(user.id)}
                        className="btn-primary text-sm"
                      >
                        Add Friend
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
            <p className="text-gray-400 text-center py-8">No users found matching "{searchQuery}"</p>
          )}
        </div>
      )}

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="space-y-6">
          {/* Incoming Bookings */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-3">Incoming Requests</h2>
            {incomingBookings.length === 0 ? (
              <p className="text-gray-400 text-sm">No incoming booking requests</p>
            ) : (
              <div className="space-y-3">
                {incomingBookings.map((booking) => (
                  <div key={booking.id} className="card flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {booking.booker_profile_image ? (
                        <img src={getImageUrl(booking.booker_profile_image) || ''} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#252550] flex items-center justify-center text-xl">
                          {booking.booker_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="text-white font-medium">{booking.booker_name}</h3>
                        <p className="text-sm text-gray-400">
                          {format(parseISO(booking.scheduled_date), 'EEE, MMM d')} at {booking.scheduled_time.slice(0, 5)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          booking.status === 'pending' ? 'bg-yellow-900/30 text-yellow-300' :
                          booking.status === 'confirmed' ? 'bg-green-900/30 text-green-300' :
                          'bg-gray-900/30 text-gray-300'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => confirmBooking(booking.id)}
                            className="btn-primary text-sm"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => declineBooking(booking.id)}
                            className="btn-secondary text-sm"
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => cancelBooking(booking.id)}
                          className="btn-secondary text-sm"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing Bookings */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-3">Your Bookings</h2>
            {outgoingBookings.length === 0 ? (
              <p className="text-gray-400 text-sm">No outgoing bookings</p>
            ) : (
              <div className="space-y-3">
                {outgoingBookings.map((booking) => (
                  <div key={booking.id} className="card flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {booking.host_profile_image ? (
                        <img src={getImageUrl(booking.host_profile_image) || ''} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#252550] flex items-center justify-center text-xl">
                          {booking.host_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="text-white font-medium">{booking.host_name}</h3>
                        <p className="text-sm text-gray-400">
                          {format(parseISO(booking.scheduled_date), 'EEE, MMM d')} at {booking.scheduled_time.slice(0, 5)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          booking.status === 'pending' ? 'bg-yellow-900/30 text-yellow-300' :
                          booking.status === 'confirmed' ? 'bg-green-900/30 text-green-300' :
                          'bg-gray-900/30 text-gray-300'
                        }`}>
                          {booking.status === 'pending' ? 'Awaiting confirmation' : booking.status}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => cancelBooking(booking.id)}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
